/**
 * Danh sách các ngôn ngữ được hỗ trợ cho tạo bài viết
 * Quản lý tập trung các ngôn ngữ và thông tin liên quan
 */

export const SUPPORTED_LANGUAGES = [
  {
    value: "vi",
    label: "Tiếng Việt",
    flag: "🇻🇳",
    nativeName: "Tiếng Việt",
    region: "Việt Nam",
    isDefault: true,
  },
  {
    value: "en",
    label: "English",
    flag: "🇺🇸",
    nativeName: "English",
    region: "United States",
    isDefault: false,
  },
  {
    value: "zh",
    label: "中文",
    flag: "🇨🇳",
    nativeName: "中文",
    region: "中国",
    isDefault: false,
  },
  {
    value: "ja",
    label: "日本語",
    flag: "🇯🇵",
    nativeName: "日本語",
    region: "日本",
    isDefault: false,
  },
  {
    value: "ko",
    label: "한국어",
    flag: "🇰🇷",
    nativeName: "한국어",
    region: "대한민국",
    isDefault: false,
  },
  {
    value: "th",
    label: "ไทย",
    flag: "🇹🇭",
    nativeName: "ภาษาไทย",
    region: "ประเทศไทย",
    isDefault: false,
  },
  {
    value: "id",
    label: "Bahasa Indonesia",
    flag: "🇮🇩",
    nativeName: "Bahasa Indonesia",
    region: "Indonesia",
    isDefault: false,
  },
  {
    value: "ms",
    label: "Bahasa Melayu",
    flag: "🇲🇾",
    nativeName: "Bahasa Melayu",
    region: "Malaysia",
    isDefault: false,
  },
];

/**
 * Cấu hình mặc định cho ngôn ngữ
 */
export const DEFAULT_LANGUAGE = "vi";

/**
 * Lấy thông tin ngôn ngữ từ mã ngôn ngữ
 * @param {string} languageCode - Mã ngôn ngữ (vi, en, zh, ...)
 * @returns {object} Thông tin ngôn ngữ hoặc ngôn ngữ mặc định
 */
export const getLanguageInfo = (languageCode) => {
  const language = SUPPORTED_LANGUAGES.find(
    (lang) => lang.value === languageCode
  );
  return (
    language ||
    SUPPORTED_LANGUAGES.find((lang) => lang.isDefault) ||
    SUPPORTED_LANGUAGES[0]
  );
};

/**
 * Lấy tên hiển thị của ngôn ngữ
 * @param {string} languageCode - Mã ngôn ngữ
 * @returns {string} Tên hiển thị của ngôn ngữ
 */
export const getLanguageName = (languageCode) => {
  const language = getLanguageInfo(languageCode);
  return language.label;
};

/**
 * Lấy tên native của ngôn ngữ
 * @param {string} languageCode - Mã ngôn ngữ
 * @returns {string} Tên native của ngôn ngữ
 */
export const getLanguageNativeName = (languageCode) => {
  const language = getLanguageInfo(languageCode);
  return language.nativeName;
};

/**
 * Lấy flag emoji của ngôn ngữ
 * @param {string} languageCode - Mã ngôn ngữ
 * @returns {string} Flag emoji
 */
export const getLanguageFlag = (languageCode) => {
  const language = getLanguageInfo(languageCode);
  return language.flag;
};

/**
 * Kiểm tra ngôn ngữ có được hỗ trợ hay không
 * @param {string} languageCode - Mã ngôn ngữ
 * @returns {boolean} True nếu được hỗ trợ
 */
export const isLanguageSupported = (languageCode) => {
  return SUPPORTED_LANGUAGES.some((lang) => lang.value === languageCode);
};

/**
 * Lấy danh sách tất cả mã ngôn ngữ được hỗ trợ
 * @returns {string[]} Mảng các mã ngôn ngữ
 */
export const getSupportedLanguageCodes = () => {
  return SUPPORTED_LANGUAGES.map((lang) => lang.value);
};

/**
 * Nhóm ngôn ngữ theo khu vực (có thể mở rộng sau này)
 */
export const LANGUAGE_GROUPS = {
  ASIA_SOUTHEAST: ["vi", "th", "id", "ms"],
  ASIA_EAST: ["zh", "ja", "ko"],
  WESTERN: ["en"],
};

/**
 * Cấu hình đặc biệt cho từng ngôn ngữ (có thể mở rộng)
 */
export const LANGUAGE_CONFIGS = {
  vi: {
    direction: "ltr",
    charset: "UTF-8",
    dateFormat: "DD/MM/YYYY",
    currency: "VND",
  },
  en: {
    direction: "ltr",
    charset: "UTF-8",
    dateFormat: "MM/DD/YYYY",
    currency: "USD",
  },
  zh: {
    direction: "ltr",
    charset: "UTF-8",
    dateFormat: "YYYY/MM/DD",
    currency: "CNY",
  },
  ja: {
    direction: "ltr",
    charset: "UTF-8",
    dateFormat: "YYYY/MM/DD",
    currency: "JPY",
  },
  ko: {
    direction: "ltr",
    charset: "UTF-8",
    dateFormat: "YYYY/MM/DD",
    currency: "KRW",
  },
  th: {
    direction: "ltr",
    charset: "UTF-8",
    dateFormat: "DD/MM/YYYY",
    currency: "THB",
  },
  id: {
    direction: "ltr",
    charset: "UTF-8",
    dateFormat: "DD/MM/YYYY",
    currency: "IDR",
  },
  ms: {
    direction: "ltr",
    charset: "UTF-8",
    dateFormat: "DD/MM/YYYY",
    currency: "MYR",
  },
};
